// ONE IDEMPOTENCY-KEY PER ATTEMPT, NOT PER PRESS (spec 09 §4.2's and §4.13's
// E2-2a1 annotations). A single-verb form whose write the reader may press
// again after a failure (the Notes panel's Add, the Tags card's New tag) keeps
// its key until the attempt is settled, so a second press after a lost
// response is answered from the server's idempotency store instead of
// writing a second note or meeting its own tag as `TAG_NAME_TAKEN`.
//
// WHEN THE KEY IS KEPT. Exactly when nothing was stored under it
// (`apps/api/src/platform/idempotency.interceptor.ts`): no answer at all (a
// lost response, a network cut, a 401, which `unwrap` throws as
// `UnauthorizedError` before any problem is read), a 5xx, whose key the layer
// releases, and 409 `IDEMPOTENCY_IN_PROGRESS`, whose first request may still be
// running. The same rule decides `failed` in `lib/bulk.ts`.
//
// WHEN A NEW KEY IS MINTED. After a success, after any other 4xx (the layer
// stores it and would replay the same refusal after its cause is fixed), and
// whenever the request body changes (the caller calls `reset` from the
// field's onChange), because the same key under a different body answers 409
// `IDEMPOTENCY_KEY_REUSE`.

import { useRef } from "react";
import { RequestError, newIdempotencyKey } from "../../../../lib/request";

/** Did this failure leave nothing stored under its key, so that a resend
 * under the same key is answered rather than refused? */
export function keyOutlivesFailure(error: unknown): boolean {
  if (!(error instanceof RequestError)) return true;
  if (error.httpStatus >= 500) return true;
  return error.code === "IDEMPOTENCY_IN_PROGRESS";
}

export interface AttemptKey {
  /** The key for this press: the attempt's own, minted on its first press. */
  take: () => string;
  /** The press failed: keep the key only when nothing was stored under it. */
  failed: (error: unknown) => void;
  /** The attempt ended (a success) or its body changed: the next press is a
   * new attempt. */
  reset: () => void;
}

/** `mint` is injectable for tests. */
export function useAttemptKey(mint: () => string = newIdempotencyKey): AttemptKey {
  const key = useRef<string | null>(null);
  return {
    take: () => {
      key.current ??= mint();
      return key.current;
    },
    failed: (error) => {
      if (!keyOutlivesFailure(error)) key.current = null;
    },
    reset: () => {
      key.current = null;
    },
  };
}
